import React, { useState, useEffect, useRef, useCallback } from "react";
import { Box, TextField, Button, CircularProgress, InputAdornment } from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import AddIcon from "@material-ui/icons/Add";
import CheckIcon from "@material-ui/icons/Check";
import { searchGoogleFonts } from "../../../../api/googleFonts";
import { buildGoogleFontsCssUrl, injectEscapeRoomFontLinks } from "./escapeRoomEditorCanvasInteraction";

function useDebouncedValue(value, delayMs = 320) {
	const [debounced, setDebounced] = useState(value);
	useEffect(() => {
		const t = setTimeout(() => setDebounced(value), delayMs);
		return () => clearTimeout(t);
	}, [value, delayMs]);
	return debounced;
}

export default function GoogleFontsSearchPicker({ onUseFont, isFontAlreadyAdded }) {
	const [query, setQuery] = useState("");
	const debouncedQuery = useDebouncedValue(query, 300);
	const [results, setResults] = useState([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState("");
	const previewLoadedRef = useRef(new Set());

	const loadPreviewFont = useCallback((family) => {
		const name = String(family || "").trim();
		if (!name || previewLoadedRef.current.has(name)) return;
		const url = buildGoogleFontsCssUrl(name);
		if (!url) return;
		previewLoadedRef.current.add(name);
		injectEscapeRoomFontLinks([url]);
	}, []);

	useEffect(() => {
		let cancelled = false;
		setLoading(true);
		setError("");
		searchGoogleFonts(debouncedQuery, 50)
			.then((list) => {
				if (cancelled) return;
				setResults(Array.isArray(list) ? list : []);
				(list || []).slice(0, 12).forEach((item) => loadPreviewFont(item.family));
			})
			.catch(() => {
				if (!cancelled) {
					setResults([]);
					setError("No se pudo buscar en Google Fonts. Comprueba la conexión e inténtalo de nuevo.");
				}
			})
			.finally(() => {
				if (!cancelled) setLoading(false);
			});
		return () => {
			cancelled = true;
		};
	}, [debouncedQuery, loadPreviewFont]);

	const handleUse = (item) => {
		if (!item?.family) return;
		loadPreviewFont(item.family);
		onUseFont(item.family, item.category || "Sans Serif");
	};

	return (
		<Box p={1.5} borderRadius={10} style={{ border: "1px solid #e2e8f0", background: "#fff" }}>
			<Box fontSize={13} fontWeight={600} color="#334155" mb={0.75}>
				Buscar en Google Fonts
			</Box>
			<Box fontSize={12} color="#64748b" mb={1.25}>
				Escribe el nombre de la fuente y pulsa «Usar». No hace falta ir a la web de Google.
			</Box>
			<TextField
				size="small"
				variant="outlined"
				fullWidth
				placeholder="Ej: Roboto, Pacifico, Montserrat…"
				value={query}
				onChange={(e) => setQuery(e.target.value)}
				autoFocus
				InputProps={{
					startAdornment: (
						<InputAdornment position="start">
							<SearchIcon style={{ fontSize: 20, color: "#94a3b8" }} />
						</InputAdornment>
					),
					endAdornment: loading ? (
						<InputAdornment position="end">
							<CircularProgress size={18} />
						</InputAdornment>
					) : null,
				}}
			/>
			{error && (
				<Box mt={1} fontSize={12} color="#dc2626">
					{error}
				</Box>
			)}
			<Box
				mt={1.25}
				style={{
					maxHeight: 240,
					overflowY: "auto",
					border: "1px solid #e2e8f0",
					borderRadius: 8,
					background: "#f8fafc",
				}}
			>
				{!loading && results.length === 0 && !error && (
					<Box p={2} fontSize={12} color="#94a3b8" textAlign="center">
						{debouncedQuery ? "Sin resultados. Prueba otro nombre." : "Escribe para buscar o espera las fuentes populares."}
					</Box>
				)}
				{results.map((item) => {
					const stack = `'${item.family}', sans-serif`;
					const added = isFontAlreadyAdded?.(item.family, item.category);
					return (
						<Box
							key={item.family}
							display="flex"
							alignItems="center"
							justifyContent="space-between"
							px={1.25}
							py={1}
							style={{
								gap: 8,
								borderBottom: "1px solid #e2e8f0",
								background: "#fff",
							}}
							onMouseEnter={() => loadPreviewFont(item.family)}
						>
							<Box style={{ minWidth: 0, flex: 1 }}>
								<Box
									fontSize={18}
									fontWeight={600}
									color="#1e293b"
									style={{ fontFamily: stack, lineHeight: 1.2 }}
								>
									{item.family}
								</Box>
								<Box fontSize={10} color="#94a3b8">
									{item.category || "Sans Serif"}
								</Box>
							</Box>
							<Button
								size="small"
								variant={added ? "outlined" : "contained"}
								color="primary"
								disabled={added}
								onClick={() => handleUse(item)}
								startIcon={added ? <CheckIcon /> : <AddIcon />}
								style={{
									textTransform: "none",
									flexShrink: 0,
									boxShadow: added ? "none" : undefined,
									fontSize: 12,
								}}
							>
								{added ? "Añadida" : "Usar"}
							</Button>
						</Box>
					);
				})}
			</Box>
		</Box>
	);
}
